import "./ResightingAssignments.css";
import { useContext, useEffect, useState } from "react";
import { Alert, Card, CardContent, Snackbar } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../Context/AppContext";
import ResightingActivitySection from "./ResightingActivitySection";
import ResightingAvifaunaDetails from "./ResightingAvifaunaDetails";
import ResightingRecordDetails from "./ResightingRecordDetails";
import { parseAvifaunaRingLookup } from "./avifaunaRecordDetailsUtils";
import {
  buildRecordDetailsFromApi,
  formatQcStatus,
  getQcAccessStatus,
} from "./recordDetailsUtils";

function ResightingSupervisorRecord() {
  const BASE_URL_LOC = process.env.REACT_APP_SERVER_LOCATION;
  const appStates = useContext(AppContext);
  const navigate = useNavigate();
  const { recordId: recordIdFromUrl } = useParams();
  const [qcAssignment, setQcAssignment] = useState(null);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null);
  const [avifaunaRingNumber, setAvifaunaRingNumber] = useState("");
  const [avifaunaRingSize, setAvifaunaRingSize] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [reviewDecision, setReviewDecision] = useState("");
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [isSavingDecision, setIsSavingDecision] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState("");

  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleBack = () => {
    navigate("/home/resighting/supervisor");
  };

  useEffect(() => {
    if (!recordIdFromUrl) {
      return;
    }

    const parsedRecordId = Number(recordIdFromUrl);
    if (!Number.isInteger(parsedRecordId) || parsedRecordId <= 0) {
      setPageError("Invalid record selected.");
      setQcAssignment(null);
      setSelectedRecordDetails(null);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setPageError("");
      setQcAssignment(null);
      setSelectedRecordDetails(null);
      setAvifaunaRingNumber("");
      setAvifaunaRingSize("");
      setReviewDecision("");
      setReviewRemarks("");

      try {
        const authToken = appStates.getToken();
        if (!authToken) {
          setPageError("Session expired. Please log in again.");
          return;
        }

        const headers = {
          "authentication-token": authToken,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "authentication-token",
          "Access-Control-Expose-Headers": "authentication-token",
        };

        const detailResponse = await fetch(
          `${BASE_URL_LOC}/api/resighting/${parsedRecordId}`,
          {
            method: "GET",
            redirect: "follow",
            headers,
            mode: "cors",
          }
        );
        const detailBody = await detailResponse.json();
        if (!detailResponse.ok) {
          setPageError(
            detailBody?.error || "Unable to load this record right now."
          );
          return;
        }

        if (!detailBody?.data) {
          setPageError("No details found for this record.");
          return;
        }

        if (!detailBody.data.resighting_details) {
          setPageError("No details found for this record.");
          return;
        }

        setQcAssignment(detailBody.data.qc_assignment ?? null);
        setSelectedRecordDetails(
          buildRecordDetailsFromApi(detailBody.data, {
            detailDateFormatOptions: { month: "short", day: "2-digit" },
          })
        );

        const { ringNumber, ringSize } = parseAvifaunaRingLookup(
          detailBody.data.resighting_details
        );
        setAvifaunaRingNumber(ringNumber);
        setAvifaunaRingSize(ringSize);
      } catch (error) {
        setPageError("Unable to load this record right now.");
      } finally {
        setIsLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordIdFromUrl]);

  useEffect(() => {
    if (!recordIdFromUrl) {
      return;
    }

    const parsedRecordId = Number(recordIdFromUrl);
    if (!Number.isInteger(parsedRecordId) || parsedRecordId <= 0) {
      return;
    }

    let cancelled = false;
    setAssignmentLogs([]);
    setLogsError("");
    setIsLoadingLogs(true);

    (async () => {
      try {
        const authToken = appStates.getToken();
        if (!authToken) {
          if (!cancelled) {
            setLogsError("Session expired. Please log in again.");
          }
          return;
        }

        const headers = {
          "authentication-token": authToken,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "authentication-token",
          "Access-Control-Expose-Headers": "authentication-token",
        };

        const response = await fetch(
          `${BASE_URL_LOC}/api/resighting/${parsedRecordId}/logs`,
          {
            method: "GET",
            redirect: "follow",
            headers,
            mode: "cors",
          }
        );

        const body = await response.json();
        if (cancelled) {
          return;
        }

        if (!response.ok) {
          setLogsError(body?.error || "Unable to load activity for this record.");
          return;
        }

        setAssignmentLogs(Array.isArray(body?.data) ? body.data : []);
      } catch (error) {
        if (!cancelled) {
          setLogsError("Unable to load activity for this record.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingLogs(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordIdFromUrl]);

  const parsedUrlRecordId =
    recordIdFromUrl && !Number.isNaN(Number(recordIdFromUrl))
      ? Number(recordIdFromUrl)
      : null;
  const selectedRecordId = parsedUrlRecordId;
  const qcAccessStatus = getQcAccessStatus(qcAssignment?.access);

  const handleSaveDecision = async () => {
    if (!reviewDecision) {
      showToast("Please select a decision before saving.", "warning");
      return;
    }

    if (!selectedRecordId) {
      return;
    }

    const authToken = appStates.getToken();
    if (!authToken) {
      showToast("Session expired. Please log in again.", "error");
      return;
    }

    setIsSavingDecision(true);
    try {
      const response = await fetch(
        `${BASE_URL_LOC}/api/resighting/${selectedRecordId}/researcher/submission`,
        {
          method: "PUT",
          redirect: "follow",
          headers: {
            "Content-Type": "application/json",
            "authentication-token": authToken,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "authentication-token",
            "Access-Control-Expose-Headers": "authentication-token",
          },
          mode: "cors",
          body: JSON.stringify({
            review_decision: reviewDecision,
            review_remarks: reviewRemarks.trim() || null,
          }),
        }
      );

      const body = await response.json();
      const backendMessage =
        body?.message ||
        body?.data?.message ||
        (typeof body?.data === "string" ? body.data : "") ||
        body?.error;

      if (!response.ok) {
        showToast(backendMessage || "Unable to save decision right now.", "error");
        return;
      }

      showToast(backendMessage || "Decision saved successfully.", "success");
      navigate("/home/resighting/supervisor");
    } catch (error) {
      showToast("Unable to save decision right now.", "error");
    } finally {
      setIsSavingDecision(false);
    }
  };

  return (
    <div className="resighting-qc-page">
      <div
        className="resighting-qc-detail-header-wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="resighting-researcher-inspect-head">
          <h2 className="resighting-qc-section-title">
            Inspect record #{parsedUrlRecordId ?? "—"}
          </h2>
          <p className="resighting-researcher-submitted-by">
            QC (intern): {qcAssignment?.qc_email || "—"}
            {qcAccessStatus ? (
              <span
                className={`resighting-qc-access-capsule resighting-qc-access-capsule--${
                  qcAccessStatus.isActive ? "active" : "inactive"
                }`}
              >
                {qcAccessStatus.label}
              </span>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          className="resighting-qc-btn"
          onClick={handleBack}
          disabled={isSavingDecision}
        >
          Back
        </button>
      </div>

      {qcAssignment?.qc_status ? (
        <div className="resighting-qc-card">
          <span className="resighting-researcher-status-label">Intern marked:</span>{" "}
          {formatQcStatus(qcAssignment.qc_status)}
          {qcAssignment.qc_remarks ? <p style={{margin: '0px', paddingTop: '5px', fontSize: '0.9rem'}}>Intern Remarks: {qcAssignment.qc_remarks}</p> : null}
        </div>
      ) : null}

      {/* {
        qcAssignment?.admin_remarks && (
          <p className="resighting-qc-blocker-remarks-wrap" style={{marginTop: '0px'}}>
            <span className="resighting-qc-remarks-label" style={{ display: "inline-block" }}>
              Admin&apos;s remarks:{" "}
            </span>
            {qcAssignment?.admin_remarks || "-"}
          </p>
        )
      } */}

      <ResightingActivitySection
        logs={assignmentLogs}
        isLoading={isLoadingLogs}
        errorMessage={logsError}
      />

      {isLoading ? (
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">Loading record details...</p>
          </CardContent>
        </Card>
      ) : pageError ? (
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">{pageError}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <ResightingRecordDetails record={selectedRecordDetails} />
          <ResightingAvifaunaDetails
            ringNumber={avifaunaRingNumber}
            ringSize={avifaunaRingSize}
            digitizedRecord={selectedRecordDetails}
          />
        </>
      )}

      {!isLoading &&
      !pageError &&
      qcAssignment?.status === "submitted_to_supervisor" ? (
        <div className="resighting-qc-card">
          <h3 className="resighting-admin-heading">Decision</h3>
          <div className="resighting-qc-radio-group">
            <label className="resighting-qc-radio-option">
              <input
                type="radio"
                name="researcher-decision"
                value="checked"
                checked={reviewDecision === "checked"}
                onChange={(e) => setReviewDecision(e.target.value)}
              />
              Checked - work looks good, approve
            </label>
            <label className="resighting-qc-radio-option">
              <input
                type="radio"
                name="researcher-decision"
                value="revert"
                checked={reviewDecision === "revert"}
                onChange={(e) => setReviewDecision(e.target.value)}
              />
              Revert - needs to be redone (returns to pool)
            </label>
          </div>

          <div className="resighting-qc-remarks-wrap">
            <label className="resighting-qc-record-id" htmlFor="researcher-remarks">
              Remarks
            </label>
            <textarea
              id="researcher-remarks"
              className="resighting-qc-remarks-input"
              style={{ marginTop: "0.5rem", fontFamily: "inherit" }}
              rows={3}
              value={reviewRemarks}
              onChange={(e) => setReviewRemarks(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="resighting-researcher-actions">
            <button
              type="button"
              className="resighting-qc-btn"
              onClick={handleSaveDecision}
              disabled={isSavingDecision || !reviewDecision}
            >
              {isSavingDecision ? "Saving..." : "Save decision"}
            </button>
          </div>
        </div>
      ) : null}

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ResightingSupervisorRecord;
